import { isMorphoBlue } from '@1delta/lender-registry'
import type { TermSheetInput } from '../input'
import type { DeepPartial, TermSheet } from '../types'
import type { TermAdapter } from './index'

/**
 * Morpho Blue — the IMMUTABLE-market case.
 *
 * The generic builder leaves `governance` to the screener, which reports the
 * protocol OWNER and therefore concludes "governed". For Morpho Blue that is
 * wrong in the way that matters most: a market's parameters — loan token,
 * collateral token, ORACLE, IRM and LLTV — are fixed by `createMarket` and
 * there is no setter for any of them. Verified against the deployed ABI: the
 * entire owner-only surface is `enableIrm`, `enableLltv`, `setFee`,
 * `setFeeRecipient`, `setOwner`, and the first two only widen what a FUTURE
 * `createMarket` may choose.
 *
 * Left uncorrected, `severity.ts` raised "Parameters can be changed with no
 * notice period" on every Blue market — a warning about a risk that does not
 * exist, on the one lender family whose whole design point is that it cannot
 * happen.
 *
 * **Deliberately `isMorphoBlue`, NOT `isMorphoType`.** Lista's Moolah is a Blue
 * fork for the lending math, but its deployed surface adds `setWhiteList` and
 * `setProvider` — governance reach that Blue does not have — so it must not
 * inherit this claim.
 *
 * Two things stay mutable and are stated rather than hidden:
 *
 *  - **The protocol fee.** `setFee(marketParams, newFee)` DOES reach a live
 *    market (capped at 25 % of interest). It is the only governance power over
 *    an existing market, so it is the only one listed.
 *  - **The oracle CONTRACT.** The market's oracle ADDRESS is immutable, but
 *    what sits behind it need not be — an upgradeable or owner-controlled
 *    adapter can be repointed. That is exactly what `OracleTerms.mutability`
 *    exists to carry, and on an otherwise immutable market it is the ONLY
 *    remaining trust vector. The adapter does not assert it either way; the
 *    oracle classification fills it in per market.
 */
/** Morpho Blue's `ConstantsLib.MAX_FEE` — 25 % of interest, in PERCENT. */
export const MORPHO_BLUE_MAX_FEE_PERCENT = 25

/**
 * The market's protocol fee as a PERCENT of interest.
 *
 * The scale is producer-dependent and the type does not say which: the on-chain
 * path emits the raw WAD (`state.fee.toString()`, e.g. `"50000000000000000"`)
 * while the Morpho API returns a plain fraction (`0.05`). Both land on the same
 * `market.fee` field, so a single assumed scale is wrong for half the rows —
 * 5 % rendered as 5e16 %, or as 0.00000000000000005 %.
 *
 * Disambiguated by magnitude, which is SAFE here rather than a heuristic: the
 * protocol caps the fee at 0.25, so any value above 1 cannot be a fraction and
 * must be WAD.
 */
function feePercent(raw: unknown): number | undefined {
  if (raw == null) return undefined
  const n = typeof raw === 'string' ? Number(raw) : (raw as number)
  if (!Number.isFinite(n) || n <= 0) return undefined
  return n > 1 ? (n / 1e18) * 100 : n * 100
}

export const morphoBlueAdapter: TermAdapter = {
  id: 'morpho-blue',
  matches: (lender: string) => isMorphoBlue(lender),
  profileId: () => 'morpho.blue@v1',
  build: (input: TermSheetInput): DeepPartial<TermSheet> => {
    const market = (input.market ?? {}) as Record<string, unknown>
    const fee = feePercent(market.fee)

    return {
      governance: {
        mutability: 'immutable',
        // The ONE power that reaches an existing market. Listing the full owner
        // surface here would imply `enableLltv` could change THIS market's LLTV,
        // which is the misreading the whole adapter exists to prevent.
        powers: ['change-fees'],
      },
      supply: {
        // Stated as a FEE rather than left as a bare governance power, so the
        // bound travels with it: the number a supplier is exposed to is "0 %
        // today, never above 25 %", which a `change-fees` enum cannot express.
        //
        // Emitted even at 0 — the current Morpho default — because "this market
        // charges no fee, and the most it could ever charge is 25 %" is the
        // whole point, and an absent row would leave the power unexplained.
        fees: [
          {
            id: 'reserve-factor',
            label: 'Protocol fee on interest',
            when: 'ongoing',
            unit: 'percent',
            basis: 'yield',
            value: fee ?? 0,
            payee: 'protocol',
            mutable: true,
            cap: MORPHO_BLUE_MAX_FEE_PERCENT,
            description:
              'A share of the interest borrowers pay, taken before it reaches suppliers. Morpho governance can change it on a live market, but the contract refuses anything above 25 % — it is the only parameter of this market that can move.',
          },
        ],
      },
    }
  },
}
